<script lang="ts">
    import { createEventDispatcher, onMount } from 'svelte';
    import type { ViewConfig } from '../services/view';
    
    export let isOpen: boolean = false;
    export let view: ViewConfig | null = null;
    export let title: string = 'Create View';
    
    const dispatch = createEventDispatcher<{
      save: { name: string; description: string };
      cancel: void;
    }>();
    
    let name: string = view.name || '';
    let description: string = view.description || '';
    let isLoading: boolean = false;
    let adminLevel: 'admin' | 'facilitator' | 'none' | 'hidden' = view.adminLevel || 'none';

    
    function handleSubmit() {
      if (!name.trim()) {
        return;
      }
      
      isLoading = true;
      console.log('save', name, description, adminLevel);
      dispatch('save', { 
        name: name.trim(), 
        description: description.trim(),
        adminLevel: adminLevel // Include adminLevel in save
      });
    }
    
    function handleCancel() {
      dispatch('cancel');
    }
    
    function handleKeydown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        handleCancel();
      }
    }
    
    function handleBackdropClick(event: Event) {
      if (event.target === event.currentTarget) {
        handleCancel();
      }
    }
  </script>
  
  {#if isOpen}
    <div class="modal-backdrop" on:click={handleBackdropClick} on:keydown={handleKeydown} aria-modal="true" role="dialog">
      <div class="modal-container">
        <div class="modal-header">
          <h2>{title}</h2>
          <button class="close-btn" on:click={handleCancel} title="Close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 6l12 12M6 18L18 6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
        
        <form on:submit|preventDefault={handleSubmit} class="modal-form">
          <div class="form-group">
            <label for="view-name">Name *</label>
            <input
              id="view-name"
              type="text"
              bind:value={name}
              required
              maxlength="100"
              placeholder="Enter view name"
              disabled={isLoading}
              class="form-input"
            />
          </div>
          
          <div class="form-group">
            <label for="view-description">Description</label>
            <textarea
              id="view-description"
              bind:value={description}
              maxlength="500"
              placeholder="Enter view description (optional)"
              disabled={isLoading}
              class="form-textarea"
              rows="4"
            ></textarea>
          </div>

          <div class="form-group">
            <label for="admin-level">Visibility Level</label>
            <select
              id="admin-level"
              bind:value={adminLevel}
              disabled={isLoading}
              class="form-input admin-dropdown"
            >
              <option value="none">Public (Anyone can view)</option>
              <option value="facilitator">Facilitators & Admins</option>
              <option value="admin">Admins Only</option>
              <option value="hidden">Hidden (Not in gallery)</option>
            </select>
            <p class="help-text">
              {#if adminLevel === 'none'}
                Anyone can view this view
              {:else if adminLevel === 'facilitator'}
                Only facilitators and admins can view
              {:else if adminLevel === 'admin'}
                Only admins can view this view
              {:else if adminLevel === 'hidden'}
                Not shown in gallery, only accessible via direct link
              {/if}
            </p>
          </div>
          
          {#if view}
            <div class="form-group">
              <div class="view-info">
                <div class="info-item">
                  <span class="info-label">Created:</span>
                  <span class="info-value">{view.createdAt ? new Date(view.createdAt).toLocaleDateString() : 'N/A'}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">Created by:</span>
                  <span class="info-value">{view.createdBy || 'Unknown'}</span>
                </div>
                {#if view.updatedAt}
                  <div class="info-item">
                    <span class="info-label">Last updated:</span>
                    <span class="info-value">{new Date(view.updatedAt).toLocaleDateString()}</span>
                  </div>
                {/if}
              </div>
            </div>
          {/if}
          
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" on:click={handleCancel} disabled={isLoading}>
              Cancel
            </button>
            <button type="submit" class="btn btn-primary" disabled={!name.trim() || isLoading}>
              {#if isLoading}
                <svg class="spinner" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" stroke-dasharray="60" stroke-dashoffset="60">
                    <animateTransform attributeName="transform" type="rotate" values="0 12 12;360 12 12" dur="1s" repeatCount="indefinite"/>
                  </circle>
                </svg>
                Saving...
              {:else}
                {view ? 'Update' : 'Create'}
              {/if}
            </button>
          </div>
        </form>
      </div>
    </div>
  {/if}
  
  <style>
    .modal-backdrop {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
    }
    
    .modal-container {
      background: white;
      border-radius: 8px;
      width: 100%;
      max-width: 500px;
      max-height: 90vh;
      overflow: hidden;
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
    }
    
    .modal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 20px;
      border-bottom: 1px solid #e0e0e0;
    }
    
    .modal-header h2 {
      margin: 0;
      font-size: 20px;
      font-weight: 600;
      color: #333;
    }
    
    .close-btn {
      width: 32px;
      height: 32px;
      border: none;
      background: none;
      cursor: pointer;
      border-radius: 6px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #666;
      transition: background-color 0.2s ease;
    }
    
    .close-btn:hover {
      background: #f5f5f5;
      color: #333;
    }
    
    .modal-form {
      padding: 20px;
      overflow-y: auto;
      max-height: calc(90vh - 140px);
    }
    
    .form-group {
      margin-bottom: 20px;
    }
    
    .form-group label {
      display: block;
      margin-bottom: 6px;
      font-weight: 500;
      color: #333;
      font-size: 14px;
    }
    
    .form-input,
    .form-textarea {
      width: 100%;
      padding: 8px 12px;
      border: 1px solid #d0d0d0;
      border-radius: 6px;
      font-size: 14px;
      transition: border-color 0.2s ease;
      box-sizing: border-box;
    }
    
    .form-input:focus,
    .form-textarea:focus {
      outline: none;
      border-color: #0369a1;
      box-shadow: 0 0 0 3px rgba(3, 105, 161, 0.1);
    }
    
    .form-input:disabled,
    .form-textarea:disabled {
      background: #f5f5f5;
      color: #666;
      cursor: not-allowed;
    }
    
    .view-info {
      background: #f9fafb;
      border: 1px solid #e5e7eb;
      border-radius: 6px;
      padding: 12px;
    }
    
    .info-item {
      display: flex;
      justify-content: space-between;
      margin-bottom: 8px;
    }
    
    .info-item:last-child {
      margin-bottom: 0;
    }
    
    .info-label {
      font-weight: 500;
      color: #374151;
      font-size: 13px;
    }
    
    .info-value {
      color: #6b7280;
      font-size: 13px;
    }
    
    .modal-footer {
      display: flex;
      gap: 12px;
      justify-content: flex-end;
      padding: 20px;
      border-top: 1px solid #e0e0e0;
      background: #f9fafb;
    }
    
    .btn {
      padding: 8px 16px;
      border: none;
      border-radius: 6px;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s ease;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    
    .btn:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
    
    .btn-secondary {
      background: #f3f4f6;
      color: #374151;
      border: 1px solid #d1d5db;
    }
    
    .btn-secondary:hover:not(:disabled) {
      background: #e5e7eb;
    }
    
    .btn-primary {
      background: #0369a1;
      color: white;
    }
    
    .btn-primary:hover:not(:disabled) {
      background: #0c4a6e;
    }
    
    .spinner {
      animation: spin 1s linear infinite;
    }

    .admin-dropdown{
      padding:0px;
    }
    
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  </style>